import React, { useState, useEffect } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, collection, addDoc, query, orderBy, onSnapshot, serverTimestamp } from "firebase/firestore";
import PatientDashboard from "./Patient/PatientDashboard";
import MidwifeDashboard from "./MidwifeDashboard";

// ==================== إعدادات Firebase (خاصة بك) ====================
const firebaseConfig = {
  apiKey: "AIzaSyAYchXFOuXKRD-fZ9ycJaow6I_DkQAFBMk",
  authDomain: "rosyhealth-7f261.firebaseapp.com",
  projectId: "rosyhealth-7f261",
  storageBucket: "rosyhealth-7f261.firebasestorage.app",
  messagingSenderId: "388896933833",
  appId: "1:388896933833:web:5488a824e6b0aaa66b0a18"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const MIDWIFE_EMAIL = "RosyHealth@gmail.com";

// ==================== styleSheet ====================
const styleSheet = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --rose: #C4899A;
    --rose-light: #EDD9DF;
    --rose-dark: #8B5A6A;
    --sage: #8BA89A;
    --sage-light: #D6E5DE;
    --cream: #F5F0EA;
    --ivory: #FAF7F2;
    --charcoal: #83005e;
    --muted: #8A8078;
    --gold: #C9A96E;
  }

  .wv-page {
    font-family: 'DM Sans', sans-serif;
    background: var(--ivory);
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-x: hidden;
  }

  /* ── NAVBAR ── */
  .wv-navbar {
    background: white;
    display: flex;
    align-items: center;
    padding: 0 48px;
    height: 80px;
    border-bottom: 0.5px solid var(--rose-light);
    position: relative;
    z-index: 30;
  }

  .wv-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    flex-shrink: 0;
  }

  .wv-logo-mark {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid var(--rose);
    overflow: hidden;
  }

  .wv-logo-mark img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  .wv-logo-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px;
    font-weight: 600;
    color: var(--charcoal);
    letter-spacing: 0.02em;
    line-height: 1;
  }

  .wv-logo-name span { color: var(--rose); }

  .wv-nav-links {
    display: flex;
    align-items: center;
    gap: 0;
    margin-left: auto;
  }

  .wv-nav-link {
    text-decoration: none;
    color: #444;
    font-size: 13.5px;
    font-weight: 400;
    padding: 0 20px;
    height: 80px;
    display: flex;
    align-items: center;
    gap: 5px;
    border-bottom: 3px solid transparent;
    transition: border-color 0.2s, color 0.2s;
    white-space: nowrap;
    cursor: pointer;
  }

  .wv-nav-link:hover { color: var(--charcoal); border-bottom-color: var(--rose-light); }
  .wv-nav-link.active { color: var(--charcoal); border-bottom-color: var(--rose); }
  .wv-nav-link i { font-size: 13px; opacity: 0.5; }

  /* Mobile menu toggle */
  .wv-menu-toggle {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    margin-left: auto;
    padding: 8px;
    color: var(--charcoal);
    font-size: 26px;
    line-height: 1;
  }

  /* Mobile drawer */
  .wv-mobile-menu {
    display: none;
    flex-direction: column;
    background: white;
    border-top: 0.5px solid var(--rose-light);
    padding: 12px 0 20px;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 100;
    box-shadow: 0 8px 24px rgba(131,0,94,0.08);
  }

  .wv-mobile-menu.open { display: flex; }

  .wv-mobile-menu .wv-nav-link {
    height: auto;
    padding: 14px 28px;
    border-bottom: none;
    border-left: 3px solid transparent;
    font-size: 15px;
  }

  .wv-mobile-menu .wv-nav-link:hover {
    background: var(--ivory);
    border-left-color: var(--rose);
    border-bottom-color: transparent;
  }

  .wv-mobile-menu .wv-nav-link.active {
    border-left-color: var(--rose);
    border-bottom-color: transparent;
    background: var(--ivory);
  }

  /* ── HERO ── */
  .wv-hero {
    display: flex;
    align-items: stretch;
    min-height: 520px;
    position: relative;
    overflow: hidden;
  }

  .wv-geo-lines {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
  }

  .wv-geo-lines svg {
    width: 100%;
    height: 100%;
  }

  .wv-hero-left {
    flex: 0 0 46%;
    background: #faf7f2;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 64px 56px 64px 56px;
    position: relative;
    z-index: 5;
  }

  .wv-hero-eyebrow {
    font-size: 11.5px;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: var(--rose);
    font-weight: 500;
    margin-bottom: 18px;
  }

  .wv-hero-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: 52px;
    font-weight: 300;
    line-height: 1.12;
    color: var(--charcoal);
    letter-spacing: -0.01em;
    margin-bottom: 22px;
  }

  .wv-hero-heading em {
    font-style: italic;
    color: var(--rose-dark);
  }

  .wv-hero-desc {
    font-size: 14.5px;
    color: var(--muted);
    line-height: 1.75;
    max-width: 340px;
    font-weight: 300;
    margin-bottom: 36px;
  }

  .wv-hero-cta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: var(--rose);
    color: white;
    padding: 14px 28px;
    font-size: 13px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    border-radius: 2px;
    border: none;
    text-decoration: none;
    transition: background 0.2s;
    align-self: flex-start;
    letter-spacing: 0.03em;
  }

  .wv-hero-cta:hover { background: var(--rose-dark); }
  .wv-hero-cta i { font-size: 14px; }

  .wv-hero-right {
    flex: 1;
    position: relative;
    overflow: hidden;
    z-index: 3;
  }

  .wv-hero-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .wv-stat-card {
    position: absolute;
    background: white;
    border-radius: 10px;
    padding: 12px 18px;
    box-shadow: 0 4px 20px rgba(196,137,154,0.18);
    border: 0.5px solid #EDD9DF;
    z-index: 15;
  }

  .wv-stat-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 26px;
    font-weight: 600;
    color: var(--rose-dark);
    line-height: 1;
  }

  .wv-stat-label {
    font-size: 11px;
    color: var(--muted);
    margin-top: 4px;
    letter-spacing: 0.04em;
  }

  /* ── SECTION ── */
  .wv-section {
    padding: 72px 56px 80px;
    background: var(--ivory);
    position: relative;
    z-index: 5;
  }

  .wv-section-eyebrow {
    text-align: center;
    font-size: 11.5px;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--rose);
    font-weight: 500;
    margin-bottom: 14px;
  }

  .wv-section-heading {
    text-align: center;
    font-family: 'Cormorant Garamond', serif;
    font-size: 40px;
    font-weight: 300;
    color: var(--charcoal);
    margin-bottom: 56px;
    letter-spacing: -0.01em;
  }

  .wv-cards-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    border: 0.5px solid var(--rose-light);
  }

  .wv-card {
    padding: 36px 28px;
    border-right: 0.5px solid var(--rose-light);
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: white;
    transition: background 0.2s, transform 0.2s;
    cursor: pointer;
    position: relative;
    overflow: hidden;
  }

  .wv-card:last-child { border-right: none; }

  .wv-card::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--rose);
    transform: scaleX(0);
    transition: transform 0.25s;
  }

  .wv-card:hover { background: var(--cream); }
  .wv-card:hover::after { transform: scaleX(1); }

  .wv-card-icon {
    width: 52px;
    height: 52px;
    background: var(--rose-light);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--charcoal);
    font-size: 24px;
    flex-shrink: 0;
  }

  .wv-card-title {
    font-size: 15px;
    font-weight: 500;
    color: var(--charcoal);
    letter-spacing: 0.01em;
  }

  .wv-card-desc {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.65;
    font-weight: 300;
  }

  .wv-card-link {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--rose);
    font-weight: 500;
    letter-spacing: 0.04em;
    margin-top: auto;
    text-decoration: none;
  }

  .wv-card-link i { font-size: 14px; }

  /* ══════════════════════════════════════
     RESPONSIVE — TABLET (≤ 900px)
  ══════════════════════════════════════ */
  @media (max-width: 900px) {
    /* Navbar */
    .wv-navbar {
      padding: 0 24px;
      height: 68px;
      position: relative;
    }

    .wv-nav-links { display: none; }
    .wv-menu-toggle { display: flex; align-items: center; justify-content: center; }

    /* Hero */
    .wv-hero {
      flex-direction: column;
      min-height: auto;
    }

    .wv-hero-left {
      flex: auto;
      padding: 48px 32px 40px;
    }

    .wv-hero-heading { font-size: 38px; }
    .wv-hero-desc { max-width: 100%; }

    .wv-hero-right { height: 300px; }

    .wv-stat-card { display: none; }

    /* Section */
    .wv-section { padding: 56px 32px 64px; }
    .wv-section-heading { font-size: 32px; margin-bottom: 36px; }

    /* Cards grid — 2 cols */
    .wv-cards-grid { grid-template-columns: repeat(2, 1fr); }
    .wv-card:nth-child(2) { border-right: none; }
    .wv-card:nth-child(1),
    .wv-card:nth-child(2) { border-bottom: 0.5px solid var(--rose-light); }

    /* Contact section */
    .wv-contact-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
  }

  /* ══════════════════════════════════════
     RESPONSIVE — MOBILE (≤ 600px)
  ══════════════════════════════════════ */
  @media (max-width: 600px) {
    /* Navbar */
    .wv-navbar { padding: 0 20px; height: 62px; }

    .wv-logo-name { font-size: 22px; }
    .wv-logo-mark { width: 42px; height: 42px; }

    /* Hero */
    .wv-hero-left { padding: 36px 20px 32px; }
    .wv-hero-eyebrow { font-size: 10px; letter-spacing: 0.2em; margin-bottom: 12px; }
    .wv-hero-heading { font-size: 30px; margin-bottom: 16px; }
    .wv-hero-desc { font-size: 13.5px; margin-bottom: 28px; }
    .wv-hero-cta { padding: 12px 22px; font-size: 12.5px; }
    .wv-hero-right { height: 240px; }

    /* Section */
    .wv-section { padding: 44px 20px 52px; }
    .wv-section-eyebrow { font-size: 10px; }
    .wv-section-heading { font-size: 26px; margin-bottom: 28px; }

    /* Cards grid — 1 col */
    .wv-cards-grid { grid-template-columns: 1fr; }
    .wv-card {
      border-right: none;
      border-bottom: 0.5px solid var(--rose-light);
      padding: 28px 20px;
    }
    .wv-card:last-child { border-bottom: none; }
    .wv-card:nth-child(2) { border-right: none; }

    /* Contact */
    .wv-contact-section { padding: 44px 20px !important; }
    .wv-contact-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
    .wv-contact-title { font-size: 32px !important; }
    .wv-contact-form-wrap { padding: 28px 20px !important; border-radius: 24px !important; }
    .wv-contact-info-card { padding: 18px !important; border-radius: 20px !important; }
    .wv-contact-badge { padding: 5px 14px !important; font-size: 11px !important; }

    /* Footer */
    .wv-footer { padding: 20px 16px !important; }
  }

  /* ══════════════════════════════════════
     RESPONSIVE — SMALL MOBILE (≤ 380px)
  ══════════════════════════════════════ */
  @media (max-width: 380px) {
    .wv-hero-heading { font-size: 26px; }
    .wv-section-heading { font-size: 22px; }
    .wv-logo-name { font-size: 19px; }
    .wv-logo-mark { width: 36px; height: 36px; }
    .wv-hero-cta { width: 100%; justify-content: center; }
  }
`;

// ========== STYLES PARTAGÉS POUR LES PAGES D'AUTH (inchangés) ==========
const stylesAuth = {
  container: {
    minHeight: "100vh",
    background: "#FAF7F2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Sans', sans-serif",
    padding: "20px",
  },
  card: {
    maxWidth: "440px",
    width: "100%",
    background: "white",
    borderRadius: "24px",
    padding: "40px 32px",
    boxShadow: "0 20px 35px -10px rgba(196,137,154,0.15)",
    textAlign: "center",
    border: "1px solid #EDD9DF",
  },
  logoCircle: {
    width: "70px",
    height: "70px",
    margin: "0 auto 12px auto",
    borderRadius: "50%",
    overflow: "hidden",
    border: "2px solid #C4899A",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },
  logoImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  welcomeBack: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#83005e",
    marginBottom: "8px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#8A8078",
    marginBottom: "32px",
  },
  inputGroup: {
    textAlign: "left",
    marginBottom: "20px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "500",
    color: "#4a3b6e",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    border: "1px solid #EDD9DF",
    borderRadius: "12px",
    outline: "none",
    fontFamily: "inherit",
    transition: "border 0.2s",
  },
  options: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "28px",
    fontSize: "13px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#8A8078",
    cursor: "pointer",
  },
  checkbox: {
    width: "16px",
    height: "16px",
    cursor: "pointer",
    accentColor: "#C4899A",
  },
  forgotLink: {
    color: "#C4899A",
    textDecoration: "none",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
  },
  signInBtn: {
    width: "100%",
    background: "#83005e",
    color: "white",
    border: "none",
    padding: "12px",
    fontSize: "14px",
    fontWeight: "500",
    borderRadius: "40px",
    cursor: "pointer",
    marginBottom: "16px",
    transition: "background 0.2s",
    fontFamily: "'DM Sans', sans-serif",
  },
  googleBtn: {
    width: "100%",
    background: "white",
    color: "#83005e",
    border: "1px solid #EDD9DF",
    padding: "12px",
    fontSize: "14px",
    fontWeight: "500",
    borderRadius: "40px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "24px",
    transition: "background 0.2s",
    fontFamily: "'DM Sans', sans-serif",
  },
  googleIcon: {
    fontWeight: "bold",
    fontSize: "16px",
  },
  signupText: {
    fontSize: "13px",
    color: "#8A8078",
  },
  signupLinkButton: {
    background: "none",
    border: "none",
    color: "#C4899A",
    cursor: "pointer",
    fontWeight: "500",
    textDecoration: "underline",
    marginLeft: "4px",
    fontSize: "13px",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#8A8078",
    cursor: "pointer",
    marginTop: "16px",
    fontSize: "13px",
    textDecoration: "underline",
  },
  message: {
    marginTop: "16px",
    padding: "10px",
    borderRadius: "8px",
    fontSize: "13px",
    textAlign: "center",
  },
  successMessage: {
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
  },
  errorMessage: {
    backgroundColor: "#FFEBEE",
    color: "#C62828",
  },
};

// ========== PAGE DE CONNEXION (avec Firebase) ==========
const AuthScreen = ({ onLoginSuccess, onSwitchToSignUp, onSwitchToForgot, onBack }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      onLoginSuccess(userCredential.user.email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={stylesAuth.container}>
      <div style={stylesAuth.card}>
        <div style={stylesAuth.logoCircle}>
          <img src="/logo.png" alt="Logo" style={stylesAuth.logoImage} />
        </div>
        <h3 style={stylesAuth.welcomeBack}>Bon retour</h3>
        <p style={stylesAuth.subtitle}>Veuillez saisir vos coordonnées</p>

        <form onSubmit={handleSubmit}>
          <div style={stylesAuth.inputGroup}>
            <label style={stylesAuth.label}>Adresse e-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={stylesAuth.input}
              required
            />
          </div>
          <div style={stylesAuth.inputGroup}>
            <label style={stylesAuth.label}>Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={stylesAuth.input}
              required
            />
          </div>
          {error && <div style={{ color: "#C62828", fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <div style={stylesAuth.options}>
            <label style={stylesAuth.checkboxLabel}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={stylesAuth.checkbox}
              />
              Se souvenir pendant 30 jours
            </label>
            <button onClick={onSwitchToForgot} style={stylesAuth.forgotLink}>Mot de passe oublié ?</button>
          </div>
          <button type="submit" style={stylesAuth.signInBtn} disabled={loading}>Se connecter</button>
        </form>

        <button style={stylesAuth.googleBtn}>
          <span style={stylesAuth.googleIcon}>G</span> Se connecter avec Google
        </button>

        <p style={stylesAuth.signupText}>
          Pas encore de compte ?{" "}
          <button onClick={onSwitchToSignUp} style={stylesAuth.signupLinkButton}>S'inscrire</button>
        </p>

        <button onClick={onBack} style={stylesAuth.backBtn}>← Retour à l'accueil</button>
      </div>
    </div>
  );
};

// ========== PAGE D'INSCRIPTION (avec Firebase) ==========
const SignUpScreen = ({ onSignUpSuccess, onSwitchToLogin, onBack }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    if (password.length < 6) {
      setError("Le mot de passe doit comporter au moins 6 caractères");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, "users", userCredential.user.uid), {
        fullName,
        email,
        role: email.toLowerCase() === MIDWIFE_EMAIL.toLowerCase() ? "midwife" : "patient",
        createdAt: new Date().toISOString()
      });
      onSignUpSuccess(userCredential.user.email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={stylesAuth.container}>
      <div style={stylesAuth.card}>
        <div style={stylesAuth.logoCircle}>
          <img src="/logo.png" alt="Logo" style={stylesAuth.logoImage} />
        </div>
        <h3 style={stylesAuth.welcomeBack}>Créer un compte</h3>
        <p style={stylesAuth.subtitle}>Veuillez remplir vos coordonnées</p>

        <form onSubmit={handleSubmit}>
          <div style={stylesAuth.inputGroup}>
            <label style={stylesAuth.label}>Nom complet</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={stylesAuth.input}
              required
            />
          </div>
          <div style={stylesAuth.inputGroup}>
            <label style={stylesAuth.label}>Adresse e-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={stylesAuth.input}
              required
            />
          </div>
          <div style={stylesAuth.inputGroup}>
            <label style={stylesAuth.label}>Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={stylesAuth.input}
              required
            />
          </div>
          <div style={stylesAuth.inputGroup}>
            <label style={stylesAuth.label}>Confirmer le mot de passe</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={stylesAuth.input}
              required
            />
          </div>
          {error && <div style={{ color: "#C62828", fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <button type="submit" style={stylesAuth.signInBtn} disabled={loading}>S'inscrire</button>
        </form>

        <button style={stylesAuth.googleBtn}>
          <span style={stylesAuth.googleIcon}>G</span> S'inscrire avec Google
        </button>

        <p style={stylesAuth.signupText}>
          Déjà un compte ?{" "}
          <button onClick={onSwitchToLogin} style={stylesAuth.signupLinkButton}>Se connecter</button>
        </p>

        <button onClick={onBack} style={stylesAuth.backBtn}>← Retour à l'accueil</button>
      </div>
    </div>
  );
};

// ========== PAGE MOT DE PASSE OUBLIÉ (avec Firebase) ==========
const ForgotPasswordScreen = ({ onBackToLogin, onBackToHome }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage(`Un lien de réinitialisation a été envoyé à ${email}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={stylesAuth.container}>
      <div style={stylesAuth.card}>
        <div style={stylesAuth.logoCircle}>
          <img src="/logo.png" alt="Logo" style={stylesAuth.logoImage} />
        </div>
        <h3 style={stylesAuth.welcomeBack}>Mot de passe oublié</h3>
        <p style={stylesAuth.subtitle}>Saisissez votre e-mail pour recevoir un lien de réinitialisation</p>

        <form onSubmit={handleSubmit}>
          <div style={stylesAuth.inputGroup}>
            <label style={stylesAuth.label}>Adresse e-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={stylesAuth.input}
              required
            />
          </div>
          {error && <div style={{ color: "#C62828", fontSize: 13, marginBottom: 16 }}>{error}</div>}
          {message && <div style={{ backgroundColor: "#E8F5E9", color: "#2E7D32", padding: 10, borderRadius: 8, marginBottom: 16 }}>{message}</div>}
          <button type="submit" style={stylesAuth.signInBtn} disabled={loading}>Envoyer le lien</button>
        </form>

        <button onClick={onBackToLogin} style={stylesAuth.backBtn}>← Retour à la connexion</button>
        <button onClick={onBackToHome} style={{ ...stylesAuth.backBtn, marginTop: "8px" }}>← Retour à l'accueil</button>
      </div>
    </div>
  );
};

// ========== PAGE D'INFORMATION GÉNÉRALE POUR CHAQUE CARTE ==========
const InfoPage = ({ title, description, contentDetails, onLoginClick, onBack }) => {
  return (
    <div
      style={{
        padding: "60px 24px",
        background: "linear-gradient(135deg, #FAF7F2 0%, #F5EDE8 100%)",
        minHeight: "100vh",
        animation: "fadeIn 0.5s ease-out",
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .glow-card {
            transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
            border: 1px solid rgba(196, 137, 154, 0.2);
          }
          .glow-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 25px 40px -12px rgba(131, 0, 94, 0.25);
            border-color: rgba(196, 137, 154, 0.5);
          }
          .animated-arrow {
            transition: transform 0.2s ease;
          }
          .login-btn:hover .animated-arrow {
            transform: translateX(6px);
          }
          @media (max-width: 768px) {
            .info-card-padding {
              padding: 32px 24px !important;
            }
            .info-title {
              font-size: 36px !important;
              padding-left: 16px !important;
            }
          }
          @media (max-width: 480px) {
            .info-title { font-size: 28px !important; }
            .glow-card { padding: 28px 20px !important; border-radius: 24px !important; }
          }
        `}
      </style>

      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: "#C4899A",
          cursor: "pointer",
          marginBottom: 30,
          fontSize: 14,
          display: "flex",
          alignItems: "center",
          gap: "6px",
          transition: "gap 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.gap = "12px")}
        onMouseLeave={(e) => (e.currentTarget.style.gap = "6px")}
      >
        <i className="ti ti-arrow-left" /> Retour à l'accueil
      </button>

      <div
        className="glow-card"
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          background: "white",
          borderRadius: "32px",
          padding: "48px 48px",
          boxShadow: "0 8px 30px rgba(131, 0, 94, 0.08)",
        }}
      >
        <h1
          className="info-title"
          style={{
            fontFamily: "Cormorant Garamond, serif",
            fontSize: "52px",
            color: "#83005e",
            marginBottom: "20px",
            letterSpacing: "-0.02em",
            borderLeft: "4px solid #C4899A",
            paddingLeft: "24px",
          }}
        >
          {title}
        </h1>

        <p
          style={{
            fontSize: "17px",
            color: "#4a3b6e",
            lineHeight: 1.65,
            marginBottom: "32px",
            fontWeight: 400,
          }}
        >
          {description}
        </p>

        <div
          style={{
            background: "#FEF8F5",
            borderRadius: "24px",
            padding: "28px 32px",
            margin: "24px 0 20px",
            border: "1px solid #F5E2D8",
          }}
        >
          <h3
            style={{
              fontSize: "22px",
              color: "#83005e",
              marginBottom: "16px",
              fontFamily: "Cormorant Garamond, serif",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <i className="ti ti-info-circle" style={{ color: "#C4899A", fontSize: "26px" }} />
            À propos
          </h3>
          <p style={{ color: "#8A8078", lineHeight: 1.65, fontSize: "15px" }}>
            {contentDetails}
          </p>
        </div>

        <button
          onClick={onLoginClick}
          className="login-btn"
          style={{
            background: "linear-gradient(105deg, #83005e 0%, #A6346F 100%)",
            color: "white",
            border: "none",
            padding: "14px 32px",
            borderRadius: "50px",
            fontSize: "15px",
            fontWeight: "500",
            cursor: "pointer",
            marginTop: "24px",
            display: "inline-flex",
            alignItems: "center",
            gap: "12px",
            transition: "all 0.2s ease",
            boxShadow: "0 4px 12px rgba(131, 0, 94, 0.3)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "linear-gradient(105deg, #A6346F 0%, #83005e 100%)";
            e.currentTarget.style.transform = "scale(1.02)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(131, 0, 94, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "linear-gradient(105deg, #83005e 0%, #A6346F 100%)";
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(131, 0, 94, 0.3)";
          }}
        >
          Se connecter pour plus d'informations
          <i className="ti ti-arrow-right animated-arrow" style={{ fontSize: "16px" }} />
        </button>
      </div>
    </div>
  );
};

// ========== PAGE QUI SOMMES-NOUS ==========
const AboutScreen = ({ onBack }) => {
  return (
    <div
      style={{
        padding: "40px 20px",
        background: "radial-gradient(circle at 10% 20%, #FFF5F0 0%, #FAF7F2 100%)",
        minHeight: "100vh",
        animation: "fadeIn 0.5s ease-out",
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }
          .floating-icon {
            animation: float 3s ease-in-out infinite;
          }
          .glow-about {
            transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
            border: 1px solid rgba(196, 137, 154, 0.15);
          }
          .glow-about:hover {
            transform: translateY(-8px);
            box-shadow: 0 30px 50px -20px rgba(131, 0, 94, 0.3);
            border-color: rgba(196, 137, 154, 0.5);
          }
          .founder-card {
            transition: all 0.25s ease;
            cursor: default;
          }
          .founder-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 12px 24px -8px rgba(131, 0, 94, 0.2);
            background: white !important;
            border: 1px solid rgba(196, 137, 154, 0.4);
          }
          .value-badge {
            transition: all 0.2s ease;
            cursor: default;
          }
          .value-badge:hover {
            transform: scale(1.05);
            background: #E2D0DC !important;
            box-shadow: 0 4px 12px rgba(196, 137, 154, 0.3);
          }
          @media (max-width: 768px) {
            .about-card {
              padding: 32px 20px !important;
              border-radius: 32px !important;
            }
            .about-title {
              font-size: 36px !important;
            }
            .about-icons {
              gap: 12px !important;
              margin-bottom: 20px !important;
            }
            .about-icon-circle {
              width: 50px !important;
              height: 50px !important;
            }
            .about-icon-circle i {
              font-size: 24px !important;
            }
            .about-text {
              font-size: 15px !important;
              padding: 0 10px !important;
            }
            .about-values {
              flex-direction: column;
              align-items: stretch;
              gap: 12px !important;
            }
            .founders-grid {
              grid-template-columns: 1fr !important;
              gap: 24px !important;
            }
          }
          @media (max-width: 480px) {
            .about-title { font-size: 28px !important; }
            .about-icon-circle { width: 44px !important; height: 44px !important; }
            .about-icon-circle i { font-size: 20px !important; }
          }
        `}
      </style>

      <div style={{ maxWidth: "800px", margin: "0 auto", position: "relative" }}>
        <button
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,0.8)",
            backdropFilter: "blur(8px)",
            border: "1px solid #EDD9DF",
            borderRadius: "40px",
            padding: "8px 20px",
            color: "#83005e",
            cursor: "pointer",
            marginBottom: 30,
            fontSize: "13px",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.gap = "10px";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.8)";
            e.currentTarget.style.gap = "6px";
          }}
        >
          <i className="ti ti-arrow-left" /> Retour à l'accueil
        </button>

        <div
          className="glow-about about-card"
          style={{
            background: "white",
            borderRadius: "48px",
            padding: "40px 40px",
            boxShadow: "0 20px 40px rgba(131, 0, 94, 0.08)",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div className="about-icons" style={{ display: "flex", justifyContent: "center", gap: 20, marginBottom: 24 }}>
              <div className="floating-icon about-icon-circle" style={{ background: "#EDD9DF", borderRadius: "50%", width: 60, height: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="ti ti-crown" style={{ fontSize: 30, color: "#C4899A" }} />
              </div>
              <div className="floating-icon about-icon-circle" style={{ animationDelay: "0.5s", background: "#EDD9DF", borderRadius: "50%", width: 60, height: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="ti ti-heart" style={{ fontSize: 30, color: "#C4899A" }} />
              </div>
              <div className="floating-icon about-icon-circle" style={{ animationDelay: "1s", background: "#EDD9DF", borderRadius: "50%", width: 60, height: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="ti ti-stethoscope" style={{ fontSize: 30, color: "#C4899A" }} />
              </div>
            </div>
            <h1 className="about-title" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "48px", color: "#83005e", marginBottom: 12, letterSpacing: "-0.02em" }}>
              Qui sommes-nous ?
            </h1>
            <div style={{ width: 60, height: 3, background: "linear-gradient(90deg, #C4899A, #83005e)", margin: "0 auto 20px", borderRadius: 2 }} />
          </div>

          <div className="about-text" style={{ fontSize: "16px", lineHeight: 1.65, color: "#4a3b6e", textAlign: "center", maxWidth: "700px", margin: "0 auto" }}>
            <p style={{ marginBottom: 20 }}>
              <strong style={{ color: "#83005e" }}>Nous sommes Soltani Chams El Assil et Saidan Ikram</strong>, deux sages-femmes passionnées par la santé et le bien-être des femmes.
            </p>
            <p style={{ marginBottom: 20 }}>
              À travers ce projet, nous avons créé cette plateforme afin de faciliter le suivi des femmes porteuses de dispositifs contraceptifs, notamment le stérilet, et de leur offrir un accompagnement simple, accessible et fiable.
            </p>
            <p style={{ marginBottom: 20 }}>
              Notre objectif est de vous fournir des informations claires, des rappels de suivi ainsi que des conseils utiles pour prendre soin de votre santé au quotidien.
            </p>
            <p>
              Nous restons à votre écoute et à votre service pour vous accompagner à chaque étape.
            </p>
          </div>

          <div className="founders-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 28, marginTop: 48 }}>
            <div className="founder-card" style={{ textAlign: "center", padding: "24px 16px", background: "#FEF8F5", borderRadius: "28px", transition: "all 0.25s ease", border: "1px solid transparent" }}>
              <div style={{ width: 90, height: 90, background: "#EDD9DF", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", border: "2px solid #C4899A" }}>
                <i className="ti ti-heart" style={{ fontSize: 48, color: "#83005e" }} />
              </div>
              <h3 style={{ fontSize: 20, color: "#83005e", marginBottom: 6, fontFamily: "Cormorant Garamond, serif" }}>Chams El Assil Soltani</h3>
              <p style={{ color: "#C4899A", fontWeight: 500, marginBottom: 8, fontSize: 13 }}>Sage-femme</p>
              <p style={{ color: "#8A8078", fontSize: 13 }}>Passionnée par le suivi gynécologique et la contraception naturelle.</p>
            </div>
            <div className="founder-card" style={{ textAlign: "center", padding: "24px 16px", background: "#FEF8F5", borderRadius: "28px", transition: "all 0.25s ease", border: "1px solid transparent" }}>
              <div style={{ width: 90, height: 90, background: "#EDD9DF", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", border: "2px solid #C4899A" }}>
                <i className="ti ti-heart" style={{ fontSize: 48, color: "#83005e" }} />
              </div>
              <h3 style={{ fontSize: 20, color: "#83005e", marginBottom: 6, fontFamily: "Cormorant Garamond, serif" }}>Ikram Saidan</h3>
              <p style={{ color: "#C4899A", fontWeight: 500, marginBottom: 8, fontSize: 13 }}>Sage-femme</p>
              <p style={{ color: "#8A8078", fontSize: 13 }}>Spécialiste en accompagnement post-natal et bien-être féminin.</p>
            </div>
          </div>

          <div className="about-values" style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 40, flexWrap: "wrap" }}>
            <div className="value-badge" style={{ display: "flex", alignItems: "center", gap: 8, background: "#EDD9DF", padding: "8px 16px", borderRadius: 40, transition: "all 0.2s ease" }}>
              <i className="ti ti-heart-handshake" style={{ color: "#83005e", fontSize: 16 }} />
              <span style={{ color: "#83005e", fontWeight: 500, fontSize: 13 }}>Écoute & Bienveillance</span>
            </div>
            <div className="value-badge" style={{ display: "flex", alignItems: "center", gap: 8, background: "#EDD9DF", padding: "8px 16px", borderRadius: 40, transition: "all 0.2s ease" }}>
              <i className="ti ti-shield-check" style={{ color: "#83005e", fontSize: 16 }} />
              <span style={{ color: "#83005e", fontWeight: 500, fontSize: 13 }}>Confiance & Sûreté</span>
            </div>
            <div className="value-badge" style={{ display: "flex", alignItems: "center", gap: 8, background: "#EDD9DF", padding: "8px 16px", borderRadius: 40, transition: "all 0.2s ease" }}>
              <i className="ti ti-stethoscope" style={{ color: "#83005e", fontSize: 16 }} />
              <span style={{ color: "#83005e", fontWeight: 500, fontSize: 13 }}>Expertise médicale</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== APPLICATION PRINCIPALE ==========
function App() {
  const [activePage, setActivePage] = useState("Accueil");
  const [step, setStep] = useState("home");
  const [user, setUser] = useState(null);
  const [isMidwife, setIsMidwife] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactStatus, setContactStatus] = useState("");

  const cardsInfo = [
    {
      icon: "ti-heart-rate-monitor",
      title: "Suivi grossesse",
      shortDesc: "Un accompagnement obstétrical complet, de la conception jusqu'à l'accouchement.",
      longDesc: "Le suivi de grossesse permet de surveiller la santé de la mère et du bébé. Il inclut des consultations régulières, des échographies et des examens sanguins.",
      details: "Chez RosyHealth, nous offrons un suivi personnalisé avec des rendez-vous adaptés à vos besoins. Nos sages-femmes et obstétriciens sont là pour vous guider à chaque trimestre.",
    },
    {
      icon: "ti-pill",
      title: "Stérilet",
      shortDesc: "Gérez le suivi de votre stérilet : rappels et conseils personnalisés.",
      longDesc: "Le stérilet (DIU) est un moyen de contraception longue durée. Il nécessite un contrôle 1 mois après la pose, puis tous les 6 mois à 1 an.",
      details: "Sur notre plateforme, vous pourrez enregistrer la date de pose, recevoir des rappels automatiques et consulter des conseils médicaux.",
    },
    {
      icon: "ti-calendar-check",
      title: "Rendez-vous",
      shortDesc: "Prise de rendez-vous en ligne disponible 24h/24, 7j/7.",
      longDesc: "Nous proposons un système de réservation en ligne simple et rapide. Choisissez le créneau qui vous convient.",
      details: "Vous pouvez prendre rendez-vous avec un médecin, une sage-femme ou pour une téléconsultation.",
    },
    {
      icon: "ti-stethoscope",
      title: "Consultation",
      shortDesc: "En présentiel ou en télémédecine, selon vos besoins.",
      longDesc: "Nous offrons des consultations en cabinet ou à distance. La télémédecine est idéale pour les suivis simples.",
      details: "Connectez-vous pour voir les disponibilités, discuter avec votre médecin par chat vidéo.",
    },
  ];

  const navLinks = [
    { label: "Nos services", hasDropdown: true, action: () => { document.getElementById("services-section")?.scrollIntoView({ behavior: "smooth" }); } },
   
    { label: "Qui sommes-nous", hasDropdown: false, action: () => setStep("about") },
    { label: "Login / Sign up", hasDropdown: false, action: () => setStep("login") },
  ];

  const handleLoginSuccess = (email) => {
    if (email.toLowerCase() === MIDWIFE_EMAIL.toLowerCase()) {
      setIsMidwife(true);
      setStep("midwife");
    } else {
      setIsMidwife(false);
      setStep("patient");
    }
    setUser(email);
  };

  const handleSignUpSuccess = (email) => {
    handleLoginSuccess(email);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsMidwife(false);
      setStep("home");
    } catch (err) {
      console.error(err);
    }
  };

  const handleBackToHome = () => {
    setStep("home");
    setUser(null);
    setIsMidwife(false);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactStatus("✅ Message envoyé ! Nous vous répondrons dans les plus brefs délais.");
    setContactName("");
    setContactEmail("");
    setContactMessage("");
    setTimeout(() => setContactStatus(""), 5000);
  };

  useEffect(() => {
    if (document.getElementById("wv-styles")) return;
    const el = document.createElement("style");
    el.id = "wv-styles";
    el.textContent = styleSheet;
    document.head.appendChild(el);
  }, []);

  if (step === "login") {
    return (
      <AuthScreen
        onLoginSuccess={handleLoginSuccess}
        onSwitchToSignUp={() => setStep("signup")}
        onSwitchToForgot={() => setStep("forgot")}
        onBack={() => setStep("home")}
      />
    );
  }

  if (step === "signup") {
    return (
      <SignUpScreen
        onSignUpSuccess={handleSignUpSuccess}
        onSwitchToLogin={() => setStep("login")}
        onBack={() => setStep("home")}
      />
    );
  }

  if (step === "forgot") {
    return (
      <ForgotPasswordScreen
        onBackToLogin={() => setStep("login")}
        onBackToHome={() => setStep("home")}
      />
    );
  }

  if (step === "midwife") {
    return <MidwifeDashboard onLogout={handleLogout} />;
  }

  if (step === "patient") {
    return <PatientDashboard userEmail={user} onLogout={handleLogout} />;
  }

  if (step === "about") {
    return <AboutScreen onBack={handleBackToHome} />;
  }

  if (step && step.startsWith("info_card_")) {
    const index = parseInt(step.split("_")[2]);
    const card = cardsInfo[index];
    return (
      <InfoPage
        title={card.title}
        description={card.longDesc}
        contentDetails={card.details}
        onLoginClick={() => setStep("login")}
        onBack={handleBackToHome}
      />
    );
  }

  // ========== Page d'accueil ==========
  return (
    <div className="wv-page">
      {/* ── NAVBAR ── */}
      <nav className="wv-navbar" style={{ position: "relative" }}>
        <a href="#" className="wv-logo" onClick={(e) => { e.preventDefault(); setStep("home"); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <div className="wv-logo-mark">
            <img src="/logo.png" alt="Logo" />
          </div>
          <div className="wv-logo-name">RosyHealth</div>
        </a>

        {/* Desktop links */}
        <div className="wv-nav-links">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href="#"
              className={`wv-nav-link${activePage === link.label ? " active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActivePage(link.label);
                if (link.action) link.action();
              }}
            >
              {link.label}
              {link.hasDropdown && <i className="ti ti-chevron-down" />}
            </a>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          className="wv-menu-toggle"
          onClick={() => setMobileMenuOpen(o => !o)}
          aria-label="Menu"
        >
          <i className={`ti ${mobileMenuOpen ? "ti-x" : "ti-menu-2"}`} />
        </button>

        {/* Mobile drawer */}
        <div className={`wv-mobile-menu${mobileMenuOpen ? " open" : ""}`}>
          {navLinks.map((link) => (
            <a
              key={link.label}
              href="#"
              className={`wv-nav-link${activePage === link.label ? " active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setActivePage(link.label);
                setMobileMenuOpen(false);
                if (link.action) link.action();
              }}
            >
              {link.label}
              {link.hasDropdown && <i className="ti ti-chevron-down" />}
            </a>
          ))}
        </div>
      </nav>

      {/* ── HERO ── */}
      <div className="wv-hero">
        <div className="wv-geo-lines">
          <svg viewBox="0 0 1400 520" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="520" y1="0" x2="680" y2="520" stroke="#C4899A" strokeWidth="1" opacity="0.35" />
            <line x1="560" y1="0" x2="720" y2="520" stroke="#C4899A" strokeWidth="0.5" opacity="0.2" />
            <polygon points="1330,280 1390,315 1390,385 1330,420 1270,385 1270,315" fill="none" stroke="#C4899A" strokeWidth="1.2" opacity="0.45" />
            <polygon points="1300,340 1340,362 1340,408 1300,430 1260,408 1260,362" fill="none" stroke="#8B5A6A" strokeWidth="0.7" opacity="0.3" />
          </svg>
        </div>
        <div className="wv-hero-left">
          <div className="wv-hero-eyebrow">Santé Féminine · Bienvenue</div>
          <h1 className="wv-hero-heading">
            Prendre soin<br />
            de <em>votre santé</em><br />
            avec douceur
          </h1>
          <p className="wv-hero-desc">
            Tout ce dont votre santé a besoin — suivi obstétrical, contraception et
            consultations — auprès d'un seul cabinet médical dédié aux femmes.
          </p>
          <a href="#contact" className="wv-hero-cta">
            Prendre rendez-vous
            <i className="ti ti-arrow-right" />
          </a>
        </div>
        <div className="wv-hero-right">
          <img src="/woman-health.jpg" alt="Cabinet médical" className="wv-hero-img" />
          <div className="wv-stat-card" style={{ top: 40, left: 30 }}>
            <div className="wv-stat-label">compétence</div>
          </div>
          <div className="wv-stat-card" style={{ bottom: 60, right: 40 }}>
            <div className="wv-stat-num">500+</div>
            <div className="wv-stat-label">Patientes suivies</div>
          </div>
        </div>
      </div>

      {/* ── SERVICES ── */}
      <div id="services-section" className="wv-section">
        <div className="wv-section-eyebrow">Construire votre demain</div>
        <h2 className="wv-section-heading">Ce que nous pouvons faire pour vous</h2>
        <div className="wv-cards-grid">
          {cardsInfo.map((card, idx) => (
            <div className="wv-card" key={idx} onClick={() => setStep(`info_card_${idx}`)}>
              <div className="wv-card-icon" aria-hidden="true">
                <i className={`ti ${card.icon}`} />
              </div>
              <div className="wv-card-title">{card.title}</div>
              <div className="wv-card-desc">{card.shortDesc}</div>
              <div className="wv-card-link">
                En savoir plus <i className="ti ti-arrow-right" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CONTACT ── */}
      <div
        id="contact"
        className="wv-contact-section"
        style={{ padding: "80px 56px", background: "linear-gradient(135deg, #FAF7F2 0%, #F0E9E4 100%)" }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <div
              className="wv-contact-badge"
              style={{ display: "inline-flex", alignItems: "center", gap: "12px", background: "#EDD9DF", padding: "6px 18px", borderRadius: "60px", marginBottom: "20px" }}
            >
              <i className="ti ti-mail" style={{ fontSize: "18px", color: "#83005e" }} />
              <span style={{ fontSize: "13px", fontWeight: "500", color: "#83005e", letterSpacing: "0.5px" }}>NOUS CONTACTER</span>
            </div>
            <h2
              className="wv-contact-title"
              style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "44px", color: "#83005e", marginBottom: "16px" }}
            >
              Prenons soin de vous ensemble
            </h2>
            <p style={{ fontSize: "16px", color: "#8A8078", maxWidth: "600px", margin: "0 auto" }}>
              Une question ? Besoin d'un conseil ? Écrivez-nous, nous vous répondrons sous 24h.
            </p>
          </div>

          <div
            className="wv-contact-grid"
            style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "48px", alignItems: "center" }}
          >
            {/* Info */}
            <div>
              <div style={{ marginBottom: "32px" }}>
                <h3 style={{ fontSize: "24px", color: "#83005e", marginBottom: "16px", fontFamily: "Cormorant Garamond, serif" }}>Nos coordonnées</h3>
                <p style={{ color: "#8A8078", lineHeight: 1.6, marginBottom: "28px" }}>Vous pouvez également nous joindre directement par email ou téléphone.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  {[
                    { icon: "ti-mail", label: "Email", value: "RosyHealth@gmail.com" },
                    { icon: "ti-phone", label: "Téléphone", value: "+213 656956211" },
                    { icon: "ti-map-pin", label: "Adresse", value: "Tebessa, Algérie" },
                  ].map(item => (
                    <div key={item.label} style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                      <div style={{ width: "48px", height: "48px", background: "#EDD9DF", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i className={`ti ${item.icon}`} style={{ fontSize: "24px", color: "#83005e" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: "13px", color: "#8A8078", marginBottom: "4px" }}>{item.label}</div>
                        <div style={{ fontSize: "16px", fontWeight: "500", color: "#83005e" }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="wv-contact-info-card"
                style={{ background: "white", borderRadius: "28px", padding: "24px", border: "1px solid #EDD9DF", boxShadow: "0 8px 24px rgba(0,0,0,0.03)" }}
              >
                <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                  <i className="ti ti-clock" style={{ fontSize: "28px", color: "#C4899A" }} />
                  <div>
                    <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#83005e" }}>Horaires d'ouverture</h4>
                    <p style={{ fontSize: "13px", color: "#8A8078" }}>Lundi - Vendredi : 9h - 18h<br />Samedi : 9h - 13h</p>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  <i className="ti ti-message-heart" style={{ fontSize: "28px", color: "#C4899A" }} />
                  <div>
                    <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#83005e" }}>Réponse rapide</h4>
                    <p style={{ fontSize: "13px", color: "#8A8078" }}>Nous répondons à tous les messages sous 24h.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div>
              <div
                className="wv-contact-form-wrap"
                style={{ background: "white", borderRadius: "32px", padding: "40px", boxShadow: "0 20px 40px -12px rgba(0,0,0,0.1)", border: "1px solid #EDD9DF" }}
              >
                <form onSubmit={handleContactSubmit} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                  <div style={{ position: "relative" }}>
                    <i className="ti ti-user" style={{ position: "absolute", left: "18px", top: "50%", transform: "translateY(-50%)", fontSize: "18px", color: "#C4899A", pointerEvents: "none" }} />
                    <input
                      type="text"
                      placeholder="Votre nom complet"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      required
                      style={{ width: "100%", padding: "14px 18px 14px 48px", borderRadius: "60px", border: "1px solid #EDD9DF", background: "#FAF7F2", fontSize: "14px", outline: "none", transition: "all 0.2s" }}
                      onFocus={(e) => { e.target.style.borderColor = "#C4899A"; e.target.style.background = "white"; e.target.style.boxShadow = "0 0 0 3px rgba(196,137,154,0.2)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#EDD9DF"; e.target.style.background = "#FAF7F2"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div style={{ position: "relative" }}>
                    <i className="ti ti-mail" style={{ position: "absolute", left: "18px", top: "50%", transform: "translateY(-50%)", fontSize: "18px", color: "#C4899A", pointerEvents: "none" }} />
                    <input
                      type="email"
                      placeholder="Votre adresse email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      required
                      style={{ width: "100%", padding: "14px 18px 14px 48px", borderRadius: "60px", border: "1px solid #EDD9DF", background: "#FAF7F2", fontSize: "14px", outline: "none", transition: "all 0.2s" }}
                      onFocus={(e) => { e.target.style.borderColor = "#C4899A"; e.target.style.background = "white"; e.target.style.boxShadow = "0 0 0 3px rgba(196,137,154,0.2)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#EDD9DF"; e.target.style.background = "#FAF7F2"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <div style={{ position: "relative" }}>
                    <i className="ti ti-message" style={{ position: "absolute", left: "18px", top: "28px", fontSize: "18px", color: "#C4899A", pointerEvents: "none" }} />
                    <textarea
                      rows="5"
                      placeholder="Votre message..."
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      required
                      style={{ width: "100%", padding: "14px 18px 14px 48px", borderRadius: "24px", border: "1px solid #EDD9DF", background: "#FAF7F2", fontSize: "14px", outline: "none", resize: "vertical", transition: "all 0.2s", fontFamily: "inherit" }}
                      onFocus={(e) => { e.target.style.borderColor = "#C4899A"; e.target.style.background = "white"; e.target.style.boxShadow = "0 0 0 3px rgba(196,137,154,0.2)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#EDD9DF"; e.target.style.background = "#FAF7F2"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                  <button
                    type="submit"
                    style={{
                      background: "linear-gradient(105deg, #83005e 0%, #A6346F 100%)",
                      color: "white",
                      padding: "14px 28px",
                      fontSize: "14px",
                      fontWeight: "600",
                      border: "none",
                      borderRadius: "60px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      letterSpacing: "0.5px",
                      boxShadow: "0 4px 12px rgba(131,0,94,0.2)",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "linear-gradient(105deg, #A6346F 0%, #83005e 100%)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(131,0,94,0.3)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "linear-gradient(105deg, #83005e 0%, #A6346F 100%)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(131,0,94,0.2)"; }}
                  >
                    Envoyer le message <i className="ti ti-send" style={{ fontSize: "16px" }} />
                  </button>
                </form>
                {contactStatus && (
                  <div
                    style={{
                      marginTop: "24px",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      backgroundColor: contactStatus.includes("envoyé") ? "#E8F5E9" : "#FFEBEE",
                      color: contactStatus.includes("envoyé") ? "#2E7D32" : "#C62828",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <i className={`ti ${contactStatus.includes("envoyé") ? "ti-check" : "ti-alert-circle"}`} />
                    {contactStatus}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="wv-footer" style={{ background: "white", borderTop: "0.5px solid #EDD9DF", padding: "28px 20px", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
          <i className="ti ti-copyright" style={{ fontSize: "14px", color: "#C4899A" }} />
          <span style={{ color: "#8A8078", fontSize: "13px" }}>2026 RosyHealth — Tous droits réservés</span>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "12px" }}>
          <a href="#" style={{ color: "#C4899A", fontSize: "18px", transition: "0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.color = "#83005e")} onMouseLeave={(e) => (e.currentTarget.style.color = "#C4899A")}><i className="ti ti-brand-instagram" /></a>
          <a href="#" style={{ color: "#C4899A", fontSize: "18px", transition: "0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.color = "#83005e")} onMouseLeave={(e) => (e.currentTarget.style.color = "#C4899A")}><i className="ti ti-brand-facebook" /></a>
          <a href="#" style={{ color: "#C4899A", fontSize: "18px", transition: "0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.color = "#83005e")} onMouseLeave={(e) => (e.currentTarget.style.color = "#C4899A")}><i className="ti ti-mail" /></a>
        </div>
      </footer>
    </div>
  );
}

export default App;